import React from "react"
import { graphql, Link } from "gatsby"
import { useBreakpoint } from "gatsby-plugin-breakpoints"

import SEO from "../../components/seo"
import Layout from "../../components/layout"
import Hero from "../../components/hero"
import PostSidebar from "../../components/postSidebar"
import SimpleText from "../../components/simpleText"
import Figure from "../../components/figure"
import PostBody from "../../components/postBody"
import Form from "../../components/form"

import styles from "./events.module.scss"
import { findFirstSectionVideo } from "../../helpers/findFirstSectionVideo"

export const query = graphql`
  query($slug: String) {
    sanityEvent(slug: { current: { eq: $slug } }) {
      title
      private
      startDate(formatString: "D MMM, YYYY")
      endDate(formatString: "D MMM, YYYY")
      datePublished: _createdAt
      dateModified: _updatedAt
      startTime
      endTime
      _rawAddress
      _rawLogo(resolveReferences: { maxDepth: 2 })
      _rawBody(resolveReferences: { maxDepth: 10 })
      form {
        title
        formLink
      }
      formPosition
      seo {
        metaTitle
        canonical
        description
        noIndex
        openGraphImage {
          asset {
            url
          }
        }
      }
    }
    sanityEventListing {
      breadcrumbTitle
    }
  }
`

const EventTemplate = ({ data }) => {
  const post = data.sanityEvent
  const breakpoints = useBreakpoint()
  const logo = post._rawLogo
  const isFormBottom =
    !post.form || post.formPosition === "bottom" || breakpoints.large
  const isFormLeft = post.formPosition === "left"
  const isFormRight = post.formPosition === "right"
  const breadcrumbTitle = data.sanityEventListing.breadcrumbTitle

  const embedUrl = findFirstSectionVideo(post?._rawBody)
  const video = embedUrl && { embedUrl }

  const sidebarBody = (
    <>
      {post.startDate && (
        <p>
          <b>Start:</b>
          <br />
          {post.startDate}
          <br />
          {post.startTime}
        </p>
      )}
      {post.endDate && (
        <p>
          <b>End:</b>
          <br />
          {post.endDate}
          <br />
          {post.endTime}
        </p>
      )}
      {post._rawAddress && <SimpleText blocks={post._rawAddress} />}
      {logo && (
        <div style={{ filter: `grayscale(100%)` }}>
          <Figure {...logo} />
        </div>
      )}
    </>
  )

  const leftForm = (
    <div className="grid">
      <div className="gridItem large-4">
        <PostSidebar className={styles.sidebar}>
          {sidebarBody}
          <Form className={styles.form} height="950px" {...post.form} />
        </PostSidebar>
      </div>
      <div className="gridItem large-8">
        <PostBody content={post._rawBody} />
      </div>
    </div>
  )

  const rightForm = (
    <div className="grid">
      <div className="gridItem large-8">
        <PostBody content={post._rawBody} />
      </div>
      <div className="gridItem large-4">
        <PostSidebar className={styles.rightSidebar}>
          {sidebarBody}
          <Form className={styles.form} height="950px" {...post.form} />
        </PostSidebar>
      </div>
    </div>
  )

  const bottomForm = (
    <div className="grid">
      <div className="gridItem large-3">
        <PostSidebar>{sidebarBody}</PostSidebar>
      </div>
      <div className="gridItem large-9">
        <PostBody content={post._rawBody} form={post.form} />
      </div>
    </div>
  )

  const renderBody = () => {
    if (isFormBottom) return bottomForm
    if (isFormLeft) return leftForm
    if (isFormRight) return rightForm
    return bottomForm
  }

  return (
    <Layout>
      <SEO
        title={post.title}
        dateModified={post.dateModified}
        datePublished={post.datePublished}
        video={video}
        {...post.seo}
        noIndex={post.seo.noIndex || post.private}
      />
      <Hero title={post.title} logo={logo}>
        <p>
          <Link to="/">Home</Link> <Link to="/events/">{breadcrumbTitle}</Link>
        </p>
      </Hero>
      {renderBody()}
    </Layout>
  )
}

export default EventTemplate
